import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authGet } from '../utils/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Sparkle, 
  TrendUp, 
  TrendDown, 
  ChartLine, 
  Warning,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Clock,
  Target,
  Wallet,
  CalendarDots
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', INR: '₹', JPY: '¥'
};

export default function Insights() {
  const { user } = useAuth();
  const [insights, setInsights] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const currency = user?.currency || 'USD';
  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const fetchCachedInsights = useCallback(async () => {
    try {
      const { data } = await authGet('/api/insights/cached');
      setInsights(data);
    } catch (e) {
      console.error('Fetch cached insights error:', e);
    }
  }, []);

  const fetchFinancialData = useCallback(async () => {
    try {
      const [dashRes, compRes] = await Promise.all([
        authGet('/api/dashboard'),
        authGet('/api/budget-comparison')
      ]);
      
      const dash = dashRes.data;
      const now = new Date();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const daysPassed = now.getDate();
      const daysRemaining = daysInMonth - daysPassed;
      const dailyRate = dash.total_expenses / Math.max(daysPassed, 1);
      const projectedExpenses = dailyRate * daysInMonth;
      const savingsRate = dash.total_income > 0 ? ((dash.total_income - dash.total_expenses) / dash.total_income) * 100 : 0;
      const overBudget = compRes.data.filter(c => c.over_budget).map(c => c.category);
      
      setFinancialData({
        income: dash.total_income,
        expenses: dash.total_expenses,
        remaining: dash.remaining,
        savings_rate: savingsRate,
        daily_rate: dailyRate,
        projected_expenses: projectedExpenses,
        days_remaining: daysRemaining,
        will_run_out: dash.remaining < (dailyRate * daysRemaining),
        over_budget_categories: overBudget,
        top_categories: dash.top_category?.name ? [[dash.top_category.name, dash.top_category.amount]] : []
      });
    } catch (e) {
      console.error('Fetch financial data error:', e);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchCachedInsights(), fetchFinancialData()]).then(() => setLoading(false));
  }, [fetchCachedInsights, fetchFinancialData]);

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const { data } = await authGet('/api/insights');
      setInsights(data);
      if (!data.data_unchanged) {
        setExpanded(true);
      }
    } catch (e) {
      console.error('Generate insights error:', e);
    }
    setGenerating(false);
  };

  const formatCurrency = (amount) => {
    return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  const summary = financialData || insights?.summary || {};
  const savingsRate = summary.savings_rate || 0;
  const expenseChange = insights?.summary?.expense_change || 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">AI Insights</h1>
          <p className="text-[#9CA3AF] mt-1">Personalized financial analysis</p>
        </div>
        <Button
          onClick={generateInsights}
          disabled={generating}
          className="btn-primary flex items-center gap-2"
          data-testid="generate-insights-btn"
        >
          {generating ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Sparkle size={18} weight="duotone" />
          )}
          <span className="hidden sm:inline">{generating ? 'Generating...' : 'Generate Insights'}</span>
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card-default p-4">
          <div className="flex items-center gap-2 text-[#9CA3AF] mb-1">
            <TrendUp size={16} className="text-[#10B981]" />
            <span className="text-sm">Income</span>
          </div>
          <p className="text-xl font-semibold text-[#10B981] tabular-nums">
            {formatCurrency(summary.income)}
          </p>
        </div>
        <div className="card-default p-4">
          <div className="flex items-center gap-2 text-[#9CA3AF] mb-1">
            <TrendDown size={16} className="text-[#EF4444]" />
            <span className="text-sm">Expenses</span>
          </div>
          <p className="text-xl font-semibold text-[#EF4444] tabular-nums">
            {formatCurrency(summary.expenses)}
          </p>
        </div>
        <div className="card-default p-4">
          <div className="flex items-center gap-2 text-[#9CA3AF] mb-1">
            <Target size={16} className="text-[#8B5CF6]" />
            <span className="text-sm">Savings Rate</span>
          </div>
          <p className={`text-xl font-semibold tabular-nums ${savingsRate >= 20 ? 'text-[#10B981]' : savingsRate >= 10 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>
            {savingsRate.toFixed(1)}%
          </p>
        </div>
        <div className="card-default p-4">
          <div className="flex items-center gap-2 text-[#9CA3AF] mb-1">
            <ChartLine size={16} className={expenseChange > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'} />
            <span className="text-sm">vs Last Month</span>
          </div>
          <div className="flex items-center gap-1">
            {expenseChange > 0 ? (
              <ArrowUp size={16} className="text-[#EF4444]" />
            ) : (
              <ArrowDown size={16} className="text-[#10B981]" />
            )}
            <p className={`text-xl font-semibold tabular-nums ${expenseChange > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
              {Math.abs(expenseChange).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Warning Alerts */}
      {summary.will_run_out && (
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4 flex items-start gap-3">
          <Warning size={24} className="text-[#EF4444] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[#EF4444]">Risk Alert: May Run Out of Funds</p>
            <p className="text-sm text-[#EF4444]/80 mt-1">
              At your current spending rate of {formatCurrency(summary.daily_rate)}/day, you may run out of money before the end of the month.
              You have {summary.days_remaining} days remaining.
            </p>
          </div>
        </div>
      )}

      {summary.over_budget_categories?.length > 0 && (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 flex items-start gap-3">
          <Warning size={24} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[#F59E0B]">Over Budget Alert</p>
            <p className="text-sm text-[#F59E0B]/80 mt-1">
              You're over budget in: {summary.over_budget_categories.join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Spending Overview */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Spending Categories */}
        <div className="card-default p-5">
          <div className="flex items-center gap-2 mb-4">
            <ChartLine size={20} className="text-[#3B82F6]" />
            <h3 className="font-semibold">Top Spending Categories</h3>
          </div>
          {summary.top_categories?.length > 0 ? (
            <div className="space-y-3">
              {summary.top_categories.map(([category, amount], index) => {
                const percentage = summary.expenses > 0 ? (amount / summary.expenses) * 100 : 0;
                return (
                  <div key={category}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[#1F2937] flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span>{category}</span>
                      </div>
                      <span className="font-semibold tabular-nums text-[#F9FAFB]">
                        {formatCurrency(amount)}
                      </span>
                    </div>
                    <Progress value={percentage} className="h-1.5 bg-[#1F2937]" />
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[#9CA3AF] text-center py-4">No spending data yet</p>
          )}
        </div>

        {/* Month-over-Month Changes */}
        <div className="card-default p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDots size={20} className="text-[#8B5CF6]" />
            <h3 className="font-semibold">Category Changes</h3>
          </div>
          {summary.category_changes?.length > 0 ? (
            <div className="space-y-3">
              {summary.category_changes.slice(0, 5).map((change) => (
                <div key={change.category} className="flex items-center justify-between">
                  <span>{change.category}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#9CA3AF]">
                      {formatCurrency(change.last)} → {formatCurrency(change.current)}
                    </span>
                    <span className={`flex items-center gap-0.5 font-medium ${
                      change.change > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'
                    }`}>
                      {change.change > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                      {Math.abs(change.change).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#9CA3AF] text-center py-4">Not enough data for comparison</p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card-secondary p-4">
          <p className="text-xs text-[#9CA3AF] mb-1">Remaining This Month</p>
          <p className={`text-lg font-semibold tabular-nums ${(summary.remaining || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
            {formatCurrency(summary.remaining)}
          </p>
        </div>
        <div className="card-secondary p-4">
          <p className="text-xs text-[#9CA3AF] mb-1">Daily Spending</p>
          <p className="text-lg font-semibold tabular-nums text-[#F59E0B]">
            {formatCurrency(summary.daily_rate)}/day
          </p>
        </div>
        <div className="card-secondary p-4">
          <p className="text-xs text-[#9CA3AF] mb-1">Projected Monthly</p>
          <p className="text-lg font-semibold tabular-nums text-[#3B82F6]">
            {formatCurrency(summary.projected_expenses)}
          </p>
        </div>
        <div className="card-secondary p-4">
          <p className="text-xs text-[#9CA3AF] mb-1">Days Remaining</p>
          <p className="text-lg font-semibold tabular-nums text-[#8B5CF6]">
            {summary.days_remaining || 0} days
          </p>
        </div>
      </div>

      {/* Potential Savings */}
      {savingsRate > 0 && (
        <div className="card-default p-5 border border-[#10B981]/20">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={20} className="text-[#10B981]" />
            <h3 className="font-semibold">Savings Potential</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-[#9CA3AF]">Monthly Savings</p>
              <p className="text-xl font-semibold text-[#10B981] tabular-nums">
                {formatCurrency((summary.income || 0) - (summary.expenses || 0))}
              </p>
            </div>
            <div>
              <p className="text-sm text-[#9CA3AF]">Potential Yearly Savings</p>
              <p className="text-xl font-semibold text-[#10B981] tabular-nums">
                {formatCurrency(((summary.income || 0) - (summary.expenses || 0)) * 12)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Card */}
      <div 
        className="relative bg-[#111827] border border-[#8B5CF6]/30 rounded-2xl p-6 overflow-hidden"
        data-testid="ai-insights-content"
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/10 to-transparent pointer-events-none" />
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl gradient-ai flex items-center justify-center">
                <Sparkle size={24} weight="duotone" className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">AI Financial Analysis</h3>
                <p className="text-sm text-[#9CA3AF]">
                  {insights?.generated_at 
                    ? `Last generated ${new Date(insights.generated_at).toLocaleString()}`
                    : 'Not generated yet'
                  }
                  {insights?.data_unchanged && (
                    <span className="ml-2 text-[#10B981]">— data unchanged, using cached result</span>
                  )}
                </p>
              </div>
            </div>
          </div>
          
          {generating && (
            <div className="flex flex-col items-center py-8">
              <div className="w-12 h-12 rounded-2xl gradient-ai flex items-center justify-center mb-4 animate-pulse-glow">
                <Sparkle size={24} weight="duotone" className="text-white" />
              </div>
              <p className="text-[#9CA3AF]">Analyzing your finances...</p>
              <p className="text-sm text-[#9CA3AF] mt-1">This may take a few seconds</p>
            </div>
          )}

          {!generating && insights?.content && !insights?.error ? (
            <div className="prose prose-invert max-w-none">
              {/* Summary (always visible) */}
              <div className="text-[#F9FAFB] leading-relaxed whitespace-pre-line">
                {expanded 
                  ? insights.content 
                  : insights.content.slice(0, 400) + (insights.content.length > 400 ? '...' : '')
                }
              </div>
              {insights.content.length > 400 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="mt-4 text-sm font-medium text-[#8B5CF6] hover:text-[#A78BFA] transition-colors"
                  data-testid="expand-insights-btn"
                >
                  {expanded ? 'Show less' : 'Read full analysis'}
                </button>
              )}
            </div>
          ) : !generating ? (
            <div className="text-center py-8">
              <Warning size={48} className="text-[#9CA3AF] mx-auto mb-4" />
              <p className="text-[#9CA3AF]">
                {insights?.content === null 
                  ? 'No analysis generated yet' 
                  : 'Add some transactions to get personalized insights!'
                }
              </p>
              <p className="text-sm text-[#9CA3AF] mt-1">
                Click "Generate Insights" to get an AI-powered analysis of your spending.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
